<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useModals } from "@/utils/useModals";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";
import prizeImg from "@/assets/img/prizes/prize_2.webp";

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();
const { openModal } = useModals();

const isMobile = computed(() => storeSettings.widthWindow <= 1024);

const userPrizesList = ref([]);
const nextList = ref(null);
const offset = ref(0);
const limit = 3;

const isPrizeSertOrTicket = (prize) => {
	return prize.won_fillword_prize === true || prize.won_text_quiz_prize === true || prize.won_weekly_prize === true;
};

const showMorePrizes = async () => {
	offset.value += limit;
	const prizesData = await storeRequests.userPrizes(offset.value, limit);
	userPrizesList.value = [ ...userPrizesList.value, ...prizesData.results];
	nextList.value = prizesData.next;
};

watch(() => storeRequests.isChangedPrize, async (newValue) => {
    if (newValue) {
        const prizesData = await storeRequests.userPrizes(offset.value, limit);
        userPrizesList.value = prizesData.results;
        nextList.value = prizesData.next;
        storeRequests.isChangedPrize = false;
    }
});
onMounted(async () => {
	const prizesData = await storeRequests.userPrizes(offset.value, limit);
	userPrizesList.value = prizesData.results;
	nextList.value = prizesData.next;
});
</script>
<template>
	<div class="user-prizes">
		<h2 class="user-prizes__title title2">Мои призы</h2>
		<div class="user-prizes__holder" v-if="userPrizesList.length">
			<ul class="user-prizes__list">
				<li class="user-prizes__item" v-for="(prize, index) of userPrizesList" :key="prize.id">
					<BaseTooltip v-if="prize.prize.description" class="user-prizes__btn-info" :placement="isMobile ? 'left' : 'bottom'" :content="prize.prize.description">
						<svg width="24" height="24"><use href="#info"></use></svg>
					</BaseTooltip>
					<div class="user-prizes__image">
						<img v-if="isPrizeSertOrTicket(prize) && prize.available_prizes.length" class="full-img 1" width="330" height="330" :src="prizeImg" alt="изображение" />
						<img v-else class="full-img" width="330" height="330" :src="prize.prize.image" alt="изображение" />
					</div>
					<button v-if="isPrizeSertOrTicket(prize) && prize.available_prizes.length" @click="openModal('modalPrize', { prize: prize.available_prizes, prizeId: prize.id })" class="user-prizes__name --blue">
						<span>Выбрать приз</span>
					</button>
					<div v-if="isPrizeSertOrTicket(prize) && !prize.available_prizes.length" class="user-prizes__name">
						{{ prize.prize.title }}
					</div>
					<div v-if="!isPrizeSertOrTicket(prize) && !prize.prize.greeting_text" class="user-prizes__name">
						{{ prize.prize.title }}
					</div>
					<a v-if="!!prize.prize.greeting_text" :href="prize.prize.greeting_text" target="_blank" rel="noopener noreferrer" class="user-prizes__name --blue --link">
						<span>{{ prize.prize.title }}</span>
						<svg width="24" height="24"><use href="#copy"></use></svg>
					</a>
				</li>
			</ul>
			<button @click="showMorePrizes" v-if="nextList" class="user-prizes__more-show btn --border-grey">Показать ещё</button>
		</div>
		<div class="profile-empty" v-else>
			<p>У вас пока нет призов, загружайте чеки и выигрывайте!</p>
			<BaseButtonUploadReceipt class="profile-empty__btn btn --blue">Загрузить чек</BaseButtonUploadReceipt>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.user-prizes {
	max-width: 120rem;
	width: 100%;
	padding: 9.7rem 0 8rem;
	margin: 0 auto;
	@media (max-width: vars.$x480) {
		padding: 4rem 0 ;
	}
}
.user-prizes__holder {
	display: flex;
	align-items: center;
	flex-direction: column;
}
.user-prizes__list {
	width: 100%;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(37.8rem, 1fr));
	gap: 3.2rem;
	
	@media (max-width: vars.$l) {
		grid-template-columns: repeat(3, 1fr);
	}
	@media (max-width: vars.$t) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: vars.$xx) {
		grid-template-columns: 1fr;
		
		gap: 1.6rem;

	}
}
.user-prizes__item {
	position: relative;
	display: flex;
	align-items: center;
	flex-direction: column;
	justify-content: space-between;
	gap: 1rem;
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: var(--border-radius);
	padding: 0rem 2.4rem 3.2rem;
	@media (max-width: vars.$x480) {
		gap: 0.5rem;
		padding: 10px 24px 8px;
	}
}
.user-prizes__btn-info {
	position: absolute;
	width: 2.4rem;
	height: 2.4rem;
	top: 2.4rem;
	right: 2.4rem;
	@media (max-width: vars.$x480) {
		top: 0.7rem;
		right: 0.7rem;
	}
	svg {
		width: 100%;
		height: 100%;
		stroke: transparent;
	}
}
.user-prizes__image {
	width: 33rem;
	@media (max-width: vars.$x480) {
		width: 20rem;
	}
	@media (max-width: vars.$x393) {
		width: 13.3rem;
	}
	img {
		aspect-ratio: 1/1;
	}
}
.user-prizes__name {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	text-align: center;
	@media (max-width: vars.$x480) {
		font-size: 1.4rem;
	}

	&.--blue {
		color: #0c4ca3;
		@media (hover: hover) {
			&:hover {
				span {
					text-decoration: none;
				}
			}
		}
		span {
			text-decoration: underline;
		}
	}

	&.--link {
		display: flex;
		align-items: center;
		gap: 1.6rem;
	}
}
.user-prizes__more-show {
	margin-top: 6rem;
	@media (max-width: vars.$x480) {
		width: 100%;
		margin-top: 3.6rem;
	}
}

.profile-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	p {
		font-weight: 400;
		font-size: 2.6rem;
		line-height: 110%;
		margin-bottom: 6.4rem;
		text-align: center;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			margin-bottom: 3.2rem;
		}
	}
}
.profile-empty__btn {
	@media (max-width: vars.$x480) {
		width: 100%;
	}
}
</style>
